import React from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../ui/alert-dialog"
import { cn } from "../../../lib/utils"

/**
 * ConfirmDialog - Standardized shadcn/ui AlertDialog wrapper
 *
 * Provides consistent confirmation dialogs across the application with:
 * - Clear title and message
 * - Flexible button labels (OK/CANCEL or YES/NO)
 * - Proper semantic structure
 * - Responsive sizing
 * - Natural theme colors
 *
 * @param {string} title - Dialog title (required)
 * @param {string} [message] - Confirmation message/description
 * @param {boolean} open - Dialog open state (required)
 * @param {function} close - Callback to close dialog (required)
 * @param {function} verify - Callback when confirming action (required)
 * @param {function} [cancel] - Callback when canceling (alternative to verify logic)
 * @param {boolean} [isKyc=false] - Use YES/NO buttons instead of OK/CANCEL
 * @param {string} [width='sm'] - Dialog width: 'xs', 'sm', 'md', 'lg'
 * @param {boolean} [noCloseCall=false] - Don't auto-close dialog after verify
 */
function ConfirmDialog(props) {
  const {
    title,
    verify,
    cancel,
    open,
    close,
    message,
    width = "sm",
    noCloseCall = false,
    isKyc = false,
  } = props

  function verifyHandler() {
    verify?.()
    if (!noCloseCall) {
      close?.()
    }
  }

  function rejectHandler() {
    cancel?.()
    close?.()
  }

  // Map width to Tailwind classes
  const widthMap = {
    xs: 'sm:max-w-xs',
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
  }

  const dialogWidth = widthMap[width] || widthMap.sm

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && close?.()}>
      <AlertDialogContent className={cn(dialogWidth)}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {message && (
            <AlertDialogDescription className="text-base">
              {message}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={rejectHandler}>
            {isKyc ? "NO" : "CANCEL"}
          </AlertDialogCancel>
          <AlertDialogAction onClick={verifyHandler}>
            {isKyc ? "YES" : "OK"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default ConfirmDialog